/**
 * The reflection probes of one scene: it captures each into its layer of the scene's reflection
 * atlas once the scene settles, and shows the captured probes to each render of the scene but a
 * capture's, which Godot draws without probes (`render_forward_clustered.cpp:1631-1634`).
 */
import * as THREE from 'three';
import { captureFarPlanes, REFLECTION_ATLAS_COUNT, REFLECTION_ATLAS_SIZE, reflectionAtlasLayout, reflectionProbeOrder, ReflectionProbeUpdateMode, } from '../../godot/rendering/environment/reflectionProbe.js';
import { bakeAtlasLayers, createReflectionAtlas, } from '../../resources/sky/skyLayers.js';
import { COMMIT_FRAMES_AFTER_SETTLE } from '../../resources/ResourceLoaderContext.js';
import { compareTreeOrder } from '../contexts/TreeOrderContext.js';
import { drawOffscreen } from '../offscreen.js';
import { beginStep, endStep } from '../frameProfile/frameProfile.js';
import { observeSceneCamera, observeSceneCull, observeSceneRenderEnd } from '../sceneRenderCamera.js';
import { holdSceneRows } from '../sceneRows/sceneRows.js';
import { leaveSlot, showInSlot } from '../sharedTexture.js';
import { TrackedObjects } from '../trackedObjects.js';
import { visibleInTree } from '../visibleInTree.js';
import { captureCameras, probeCaptureOf, probeFrame } from './captureCameras.js';
import { stageCapture } from './captureStage.js';
import { ProbeRows } from './probeRows.js';
import { atlasSlot, shownProbes } from './probeSlots.js';
import { isReflectionProbe } from './reflectionProbeObject.js';
import { sameElements } from '../../utils/sameElements.js';
import { exposureOfScene, worldExposureOfScene } from '../exposure/sceneExposure.js';
import { drawAsWorld, ownEnvironmentChanges } from '../environment/sceneEnvironment.js';
import { exposureOverBaked } from '../exposure/exposureOverBaked.js';
export class SceneProbes {
    renderer;
    scene;
    probes;
    /** The capture a face draws into, a cube Godot's capture size wide, in half floats as Godot's. */
    cube = new THREE.WebGLCubeRenderTarget(REFLECTION_ATLAS_SIZE, {
        type: THREE.HalfFloatType,
        generateMipmaps: false,
        minFilter: THREE.LinearFilter,
    });
    /** Made by `update` for the probes it holds, null before the first. */
    atlas = null;
    /** What the atlas was made for: changed, it is made again. */
    atlasProbeCount = 0;
    atlasHasRealTimeProbe = false;
    /** The capture each probe holds in the atlas, written by `capture` and cleared with the atlas. */
    captures = new Map();
    /** Advanced only by `markCapturesStale`, so each probe captures again. */
    generation = 0;
    /** The rows of the drawn probes, rewritten in place by `pack`. */
    rows = new ProbeRows();
    /** The scene's rows, which the decals of the scene write too. */
    sceneRows;
    releaseSceneRows;
    /** The probes the rows hold, written only by `pack`. */
    packed = [];
    /** Frames since the scene last had a resource on its way. */
    settledFrames = 0;
    /** Whether content arrived since the probes last captured, so each captures again once it settles. */
    hasNewContent = true;
    /**
     * What the captures saw of the environment, which a sky that bakes later changes: the change count
     * of the world's own Environment and the clear colour. Written only by `markCapturesStale`.
     */
    seenOwnEnvironmentChanges = -1;
    seenClearColor = -1;
    /**
     * The exposure each probe last captured at, written only by `capture`, and only while the world
     * holds attributes: a capture without exposure leaves it (`render_forward_clustered.cpp:1714-1716`). A
     * probe is shown at the exposure over this one (`light_storage.cpp:1810-1814`).
     */
    bakedExposures = new WeakMap();
    /** The exposure the packed rows were made for, so a change of exposure packs them again. */
    packedExposure = null;
    stopObserving;
    /** The lights whose shadows a capture without shadows hid, put back once the render ends. */
    hiddenShadows = [];
    constructor(renderer, scene) {
        this.renderer = renderer;
        this.scene = scene;
        this.probes = new TrackedObjects(scene, isReflectionProbe);
        const held = holdSceneRows(scene, renderer);
        this.sceneRows = held.rows;
        this.releaseSceneRows = held.release;
        this.stopObserving = [
            observeSceneCamera(scene, (camera) => this.show(camera)),
            // After the observers that write each light's shadow for the render.
            observeSceneCull(scene, (camera) => this.hideShadows(camera)),
            observeSceneRenderEnd(scene, () => this.endRender()),
        ];
    }
    /**
     * Captures one probe that needs it, once the scene has had no resource on its way for a few
     * frames, and packs the captured probes. Answers whether it drew, so the caller draws a frame.
     */
    update(pendingResources, clearColor) {
        this.settledFrames = pendingResources > 0 ? 0 : this.settledFrames + 1;
        if (pendingResources > 0)
            this.hasNewContent = true;
        const probes = this.probes
            .current()
            .filter(visibleInTree)
            .sort((a, b) => compareTreeOrder(a.treeOrder, b.treeOrder))
            .slice(0, REFLECTION_ATLAS_COUNT);
        const atlas = this.atlasFor(probes);
        if (this.settledFrames < COMMIT_FRAMES_AFTER_SETTLE)
            return false;
        this.markCapturesStale(clearColor);
        const stale = this.canCapture() ? probes.findIndex((probe, layer) => !this.isCaptured(probe, layer)) : -1;
        if (stale !== -1) {
            beginStep(this.renderer, 'Reflection probes');
            this.capture(probes[stale], stale, atlas, clearColor);
            endStep(this.renderer, 'Reflection probes');
        }
        const captured = probes.filter((probe) => this.captures.has(probe));
        const exposure = exposureOfScene(this.scene);
        if (stale !== -1 || !sameElements(captured, this.packed) || exposure !== this.packedExposure) {
            this.pack(captured, exposure);
        }
        return stale !== -1;
    }
    dispose() {
        for (const stop of this.stopObserving)
            stop();
        this.probes.dispose();
        this.leaveSlots();
        this.atlas?.target.dispose();
        this.cube.dispose();
        this.releaseSceneRows();
    }
    /**
     * A capture draws at the exposure of the world, not of the camera (`render_forward_clustered.cpp:1714-1716`).
     * A camera that exposes the view at 0 leaves every light with no energy to rescale to the world's,
     * so a capture waits for a camera that shows the world.
     */
    canCapture() {
        return exposureOfScene(this.scene) !== 0 || worldExposureOfScene(this.scene) === 0;
    }
    /** The atlas for `probes`, made again with their count and layout, which drops every capture. */
    atlasFor(probes) {
        const hasRealTimeProbe = probes.some(({ probe }) => probe.updateMode === ReflectionProbeUpdateMode.ALWAYS);
        if (this.atlas &&
            this.atlasProbeCount === probes.length &&
            this.atlasHasRealTimeProbe === hasRealTimeProbe)
            return this.atlas;
        this.leaveSlots();
        this.atlas?.target.dispose();
        this.captures.clear();
        this.packed = [];
        this.atlas = createReflectionAtlas(reflectionAtlasLayout(hasRealTimeProbe), probes.length);
        this.atlasProbeCount = probes.length;
        this.atlasHasRealTimeProbe = hasRealTimeProbe;
        return this.atlas;
    }
    /**
     * Marks every capture stale once new content settles, or once the world's own Environment the
     * captures saw changes. A stale capture still draws, so no probe vanishes until it captures again.
     * A change of exposure marks none: Godot rescales the capture instead.
     */
    markCapturesStale(clearColor) {
        const changes = ownEnvironmentChanges(this.scene);
        const clearHex = clearColor.getHex();
        const hasNewEnvironment = changes !== this.seenOwnEnvironmentChanges || clearHex !== this.seenClearColor;
        if (!this.hasNewContent && !hasNewEnvironment)
            return;
        this.seenOwnEnvironmentChanges = changes;
        this.seenClearColor = clearHex;
        this.hasNewContent = false;
        this.generation += 1;
    }
    /**
     * Whether `object`'s capture still shows what it would capture now. A probe that updates ALWAYS
     * captures again only then too, where Godot captures it every frame, so a node that moves under
     * it is not reflected until the scene's content changes.
     */
    isCaptured(object, layer) {
        const capture = this.captures.get(object);
        return (!!capture &&
            capture.layer === layer &&
            capture.generation === this.generation &&
            capture.probe === object.probe &&
            capture.matrixWorld.equals(object.matrixWorld));
    }
    /** Draws `object`'s six faces into the cube, then bakes them into its layer of the atlas. */
    capture(object, layer, atlas, clearColor) {
        const { probe } = object;
        const capture = { isInterior: probe.interior, hasShadows: probe.enableShadows };
        const farPlanes = captureFarPlanes(probe.size, probe.originOffset, probe.maxDistance);
        const cameras = captureCameras(object.matrixWorld, probe.originOffset, farPlanes, capture);
        drawAsWorld(this.scene, () => {
            const restore = stageCapture(this.scene, probe, clearColor);
            try {
                drawOffscreen(this.renderer, () => {
                    this.renderer.autoClear = true;
                    cameras.forEach((camera, face) => {
                        this.renderer.setRenderTarget(this.cube, face);
                        this.renderer.render(this.scene, camera);
                    });
                });
            }
            finally {
                restore();
            }
        });
        const radianceFilter = probe.updateMode === ReflectionProbeUpdateMode.ALWAYS ? 'fast' : 'importance-sample';
        bakeAtlasLayers(this.renderer, this.cube.texture, atlas, layer, radianceFilter);
        const { generation } = this;
        this.captures.set(object, { layer, generation, probe, matrixWorld: object.matrixWorld.clone() });
        const worldExposure = worldExposureOfScene(this.scene);
        if (worldExposure !== null)
            this.bakedExposures.set(object, worldExposure);
    }
    /** Writes the rows of `probes` in Godot's order, shown at `exposure`, and uploads them. */
    pack(probes, exposure) {
        this.packed = probes;
        this.packedExposure = exposure;
        const drawn = reflectionProbeOrder(probes.map((object) => this.drawnProbe(object, exposure)));
        if (this.rows.pack(drawn))
            this.sceneRows.setProbes(this.rows);
    }
    drawnProbe(object, exposure) {
        const worldToLocal = probeFrame(object.matrixWorld).invert();
        const exposureNormalization = exposureOverBaked(exposure, this.bakedExposures.get(object) ?? 1);
        return {
            ...object.probe,
            worldToLocal,
            atlasLayer: this.captures.get(object).layer,
            exposureNormalization,
        };
    }
    /** Points every lit material at this scene's probes for a render, or at none for a capture's. */
    show(camera) {
        const { atlas } = this;
        if (!atlas || this.packed.length === 0 || probeCaptureOf(camera)) {
            shownProbes[0] = 0;
            return;
        }
        shownProbes[0] = this.packed.length;
        shownProbes[1] = atlas.layout.borderSize;
        shownProbes[2] = atlas.layout.layerSizes.length - 1;
        showInSlot(this.renderer, atlasSlot, atlas.target.texture);
        this.sceneRows.show();
    }
    /** Hides each light's shadow from a capture without shadows. */
    hideShadows(camera) {
        if (probeCaptureOf(camera)?.hasShadows !== false)
            return;
        this.scene.traverse((object) => {
            const { shadow } = object;
            if (!shadow)
                return;
            this.hiddenShadows.push({ shadow, intensity: shadow.intensity });
            shadow.intensity = 0;
        });
    }
    /** Leaves later renders, of this scene or another, without probes or hidden shadows. */
    endRender() {
        shownProbes[0] = 0;
        for (const { shadow, intensity } of this.hiddenShadows)
            shadow.intensity = intensity;
        this.hiddenShadows.length = 0;
    }
    /** Leaves no slot on this scene's textures before they are freed. */
    leaveSlots() {
        if (this.atlas)
            leaveSlot(this.renderer, atlasSlot, this.atlas.target.texture);
    }
}
//# sourceMappingURL=sceneProbes.js.map